export const MT_MERGE_OPERATION_STORAGE_KEY = ['mtMerge', 'Operation', 'V1'].join('')

const OPERATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const POSITIVE_ID = /^[1-9]\d*$/
const STORED_STATUSES = new Set([
  'backing_up',
  'backup_failed',
  'backup_in_doubt',
  'submitting',
  'acceptance_unknown',
  'pending',
  'applying',
  'succeeded',
  'rejected',
  'in_doubt',
])

export type StoredMergeOperation = {
  schemaVersion: 1
  operationId: string
  keepUserId: string
  mergeUserId: string
  status:
    | 'backing_up'
    | 'backup_failed'
    | 'backup_in_doubt'
    | 'submitting'
    | 'acceptance_unknown'
    | 'pending'
    | 'applying'
    | 'succeeded'
    | 'rejected'
    | 'in_doubt'
  createdAt: number
  errorCode?: string
}

export type MergeBackupClaimResult = {
  responseOk: boolean
  responseSuccess: boolean
  jobId?: unknown
  code?: unknown
}

export type MergeBackupClaimOutcome = 'accepted' | 'gate_disabled' | 'unknown'

export const classifyMergeBackupClaims = (
  results: MergeBackupClaimResult[]
): MergeBackupClaimOutcome => {
  if (results.length !== 2) return 'unknown'
  if (results.every((result) => (
    result.responseOk
    && result.responseSuccess
    && typeof result.jobId === 'string'
    && result.jobId.length > 0
  ))) {
    return 'accepted'
  }
  if (results.every((result) => (
    !result.responseOk
    && result.code === 'MT_MERGE_NEW_OPERATIONS_DISABLED'
  ))) {
    return 'gate_disabled'
  }
  return 'unknown'
}

export const parseMtMergeOperationStorage = (saved: string | null): StoredMergeOperation | null => {
  if (!saved || saved.length > 4096) return null
  try {
    const value = JSON.parse(saved)
    if (
      value?.schemaVersion !== 1
      || typeof value.operationId !== 'string'
      || !OPERATION_ID.test(value.operationId)
      || typeof value.keepUserId !== 'string'
      || !POSITIVE_ID.test(value.keepUserId)
      || typeof value.mergeUserId !== 'string'
      || !POSITIVE_ID.test(value.mergeUserId)
      || value.keepUserId === value.mergeUserId
      || !STORED_STATUSES.has(value.status)
      || !Number.isSafeInteger(value.createdAt)
    ) {
      return null
    }
    return {
      schemaVersion: 1,
      operationId: value.operationId,
      keepUserId: value.keepUserId,
      mergeUserId: value.mergeUserId,
      status: value.status,
      createdAt: value.createdAt,
      ...(typeof value.errorCode === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(value.errorCode)
        ? { errorCode: value.errorCode }
        : {}),
    } as StoredMergeOperation
  } catch {
    return null
  }
}

export const mtMergeOperationStatusMessage = (saved: string | null): string => {
  const operation = parseMtMergeOperationStorage(saved)
  if (!operation) return ''
  if (operation.status === 'succeeded' || operation.status === 'rejected') return ''

  const pair = `MT ${operation.mergeUserId} into MT ${operation.keepUserId}`
  if (operation.status === 'backup_failed') {
    return `Backup preparation for ${pair} failed before any merge was attempted. Clear operation ${operation.operationId} to start over.`
  }
  if (operation.status === 'backup_in_doubt') {
    return `Backup preparation for ${pair} is in doubt. No merge was attempted. Reconcile operation ${operation.operationId}.`
  }
  if (operation.status === 'backing_up') {
    return `Preparing immutable backups for ${pair}. Resume operation ${operation.operationId}.`
  }
  if (operation.status === 'in_doubt') {
    return `Merge ${pair} is in doubt. Do not retry it. Reconcile operation ${operation.operationId}.`
  }
  if (operation.status === 'acceptance_unknown') {
    return `Merge ${pair} has unknown acceptance. Do not resubmit operation ${operation.operationId}.`
  }
  if (operation.status === 'applying') return `Merging ${pair} - waiting for Mariana Tek...`
  return `Merging ${pair} - operation queued...`
}
